import Clickable from 'components/Common/Clickable'
import FormatCurrency from 'components/Common/FormatCurrency'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import IconButton from 'LuxKit/Button/IconButton'
import Divider from 'LuxKit/Divider'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import Group from 'components/utils/Group'
import withMountGuard from 'hocs/withMountGuard'
import { useAppSelector } from 'hooks/reduxHooks'
import getIsLoyaltyLuxPlusTieredRenewalRewardEnabled from 'loyalty/selectors/getIsLoyaltyLuxPlusTieredRenewalRewardEnabled'
import LuxPlusLogo from 'luxPlus/components/Logos/LuxPlusLogo'
import { LUXURY_PLUS, LUXURY_PLUS_PATHS, getRegionalPersonalisedSavingsThreshold } from 'luxPlus/constants/base'
import getRegionSaveAmountCopy from 'luxPlus/lib/getRegionSaveAmountCopy'
import getLuxPlusTotalValueSaved from 'luxPlus/selectors/benefits/getLuxPlusTotalValueSaved'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { getIsLuxPlusMemberOrWithinGrace } from 'luxPlus/selectors/membership'
import { getLuxPlusSubscriptionRenewalDate } from 'selectors/luxPlusSelectors'
import GeoContext from 'contexts/geoContext'
import { rem } from 'polished'
import { useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

const Container = styled.div<{ $gradient?: boolean }>`
  background: ${props => props.$gradient
    ? `linear-gradient(90deg, ${props.theme.palette.product.luxPlus.background} 0%, #4C82DF 100%)`
    : props.theme.palette.product.luxPlus.background};
  color: ${props => props.theme.palette.product.luxPlus.contrast};
  padding: ${rem(12)} ${rem(16)};
  border-radius: ${props => props.theme.borderRadius.M};
`

const FadedCaption = styled(Caption)`
  opacity: 0.7;
`

const AccountSnapshotContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
`

const FadedDivider = styled(Divider)`
  opacity: 0.3;
`

const FadedLoadingBox = styled(LoadingBox)`
  opacity: 0.3;
`

interface Props {
  onLinkClick: () => void
}

function AccountMenuLuxPlusDetailsTile({ onLinkClick }: Props) {
  const intl = useIntl()
  const totalValueSaved = useAppSelector(getLuxPlusTotalValueSaved)
  const luxPlusSubscription = useAppSelector(state => state.auth.account.luxPlus.member.subscription)
  // Drawer classification: a recoverable membership (active / awaiting renewal / expired-in-grace)
  // shows the member state; only fully-lapsed or never-subscribed accounts see the join prompt.
  const isSubscribedLuxPlusMember = useAppSelector(getIsLuxPlusMemberOrWithinGrace)
  const isTieredRenewalRewardEnabled = useAppSelector(getIsLoyaltyLuxPlusTieredRenewalRewardEnabled)
  const renewalDate = useAppSelector(getLuxPlusSubscriptionRenewalDate)
  const hasAutoRenewal = luxPlusSubscription.item?.renewalStatus === 'ALLOW_RENEWAL'
  const { currentRegionCode } = useContext(GeoContext)
  // Hide the savings figure until it clears the region's personalised-savings threshold, matching
  // the landing page.
  const showSavings = (totalValueSaved ?? 0) >= getRegionalPersonalisedSavingsThreshold(currentRegionCode)

  const headerRow = <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
    <LuxPlusLogo colour="inherit" height={12} />
    <IconButton
      kind="tertiary"
      variant="ghost"
      horizontalOutdent="end"
      size="small"
      nonInteractable
    >
      <LineAngleRightIcon />
    </IconButton>
  </Group>

  if (luxPlusSubscription.fetching) {
    return <Container $gradient={isTieredRenewalRewardEnabled}>
      <Group direction="vertical" gap={12}>
        <div>
          <LuxPlusLogo colour="inherit" height={12} />
        </div>
        <div>
          <FadedLoadingBox height={49} />
        </div>
      </Group>
    </Container>
  }

  if (luxPlusSubscription.error) return null

  // Non-members: a "join" prompt, gated behind the tiered renewal reward experience. Without the
  // flag we keep the previous behaviour of showing nothing for non-members.
  if (!isSubscribedLuxPlusMember) {
    if (!isTieredRenewalRewardEnabled) return null

    return <Container
      $gradient
      as={Clickable}
      to={`/${LUXURY_PLUS_PATHS.DEFAULT_LANDING_PAGE}`}
      onClick={onLinkClick}
    >
      <Group direction="vertical" gap={12}>
        {headerRow}
        <Group direction="vertical" gap={2}>
          <Heading variant="heading6" colour="dark-contrast">
            {intl.formatMessage({
              id: '8Yrif5r31aROIQWMQwB5',
              defaultMessage: 'Join our VIP Travel Club',
              description: 'Heading inviting a non-member to join LuxPlus+ from the account drawer tile',
            })}
          </Heading>
          <FadedCaption variant="medium">
            <FormattedMessage
              id="aDk_FdLzZ_6zn0bEQB2-"
              defaultMessage="Save {saveAmount} each year on member-only pricing"
              description="Subtext inviting a non-member to join LuxPlus+ from the account drawer tile, with a region-dynamic savings descriptor"
              values={{ saveAmount: getRegionSaveAmountCopy(currentRegionCode, intl) }}
            />
          </FadedCaption>
        </Group>
      </Group>
    </Container>
  }

  return <Container
    $gradient={isTieredRenewalRewardEnabled}
    as={Clickable}
    to={`/${LUXURY_PLUS_PATHS.ACCOUNT_MANAGEMENT_PAGE}`}
    onClick={onLinkClick}
  >
    <Group direction="vertical" gap={12}>
      {headerRow}
      {isTieredRenewalRewardEnabled && <Group direction="vertical" gap={2}>
        {showSavings && <Heading variant="heading6" colour="dark-contrast">
          <FormattedMessage
            id="2Vs0dfAhJa98fGNT3BV5"
            defaultMessage="{savings} saved"
            description="Total lifetime amount a member has saved with LuxPlus+, shown in the account drawer tile"
            values={{ savings: <FormatCurrency value={totalValueSaved ?? 0} format="roundedDollar" /> }}
          />
        </Heading>}
        <FadedCaption variant="medium">
          {!renewalDate && (
            <FormattedMessage
              id="4FcnLPv5WEdGIBBxm-PJ"
              defaultMessage="Joined {joinDate}"
              description="Membership join date shown in the LuxPlus+ account drawer tile when there is no renewal date"
              values={{
                joinDate: <FormatDateTime value={luxPlusSubscription.item!.memberSince} day="2-digit" month="short" year="numeric" />,
              }}
            />
          )}
          {!!renewalDate && hasAutoRenewal && (
            <FormattedMessage
              id="nl_AtdGcWNbsTIGiW0pM"
              defaultMessage="Joined {joinDate} · Renews {renewalDate}"
              description="Membership join date and next renewal date shown in the LuxPlus+ account drawer tile"
              values={{
                joinDate: <FormatDateTime value={luxPlusSubscription.item!.memberSince} day="2-digit" month="short" year="numeric" />,
                renewalDate: <FormatDateTime value={renewalDate} day="2-digit" month="short" year="numeric" />,
              }}
            />
          )}
          {!!renewalDate && !hasAutoRenewal && (
            <FormattedMessage
              id="mWMC9IkmknxXmfInhJFj"
              defaultMessage="Joined {joinDate} · Ends {renewalDate}"
              description="Membership join date and expiry date shown in the LuxPlus+ account drawer tile when auto-renewal is off"
              values={{
                joinDate: <FormatDateTime value={luxPlusSubscription.item!.memberSince} day="2-digit" month="short" year="numeric" />,
                renewalDate: <FormatDateTime value={renewalDate} day="2-digit" month="short" year="numeric" />,
              }}
            />
          )}
        </FadedCaption>
      </Group>}
      {!isTieredRenewalRewardEnabled && !totalValueSaved && <FadedCaption variant="medium" align="center">
        {intl.formatMessage(
          {
            id: 'SXLxufGOBF5t99JdjpMR',
            defaultMessage: 'Welcome to {programName} where you’ll save even more',
            description: 'Welcome message for new Luxury Plus members in the navigation bar',
          },
          { programName: LUXURY_PLUS.PROGRAM_NAME },
        )}
      </FadedCaption>}
      {!isTieredRenewalRewardEnabled && !!totalValueSaved && <AccountSnapshotContainer>
        <Group direction="vertical" gap={2} horizontalAlign="center">
          <FadedCaption variant="medium">
            {intl.formatMessage({
              id: '72c_xl5uS1fx5XKBexg8',
              defaultMessage: 'Savings',
              description: 'Form field label for total savings amount in the navigation bar',
            })}
          </FadedCaption>
          <Heading variant="heading6" colour="dark-contrast">
            <FormatCurrency value={totalValueSaved} format="roundedDollar" />
          </Heading>
        </Group>
        <div>
          <FadedDivider kind="primary" direction="vertical" />
        </div>
        <Group direction="vertical" gap={2} horizontalAlign="center">
          <FadedCaption variant="medium">
            {intl.formatMessage({
              id: 'Gqw1r2KnaIdwTEv9zEsP',
              defaultMessage: 'Join date',
              description: 'Label for membership join date in the date selection or calendar picker',
            })}
          </FadedCaption>
          <Heading variant="heading6" colour="dark-contrast">
            <FormatDateTime value={luxPlusSubscription.item!.memberSince} year="2-digit" month="short" day="2-digit" />
          </Heading>
        </Group>
      </AccountSnapshotContainer>}
    </Group>
  </Container>
}

export default withMountGuard(() => {
  return useAppSelector(getIsLuxPlusEnabled)
})(AccountMenuLuxPlusDetailsTile)
